import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { TooltipRenderer, Text, Box } from 'braid-design-system';
import {
  isFuture,
  differenceInSeconds,
  formatDistanceToNowStrict as distanceInWordsToNowStrict,
} from 'date-fns';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import translations from './.vocab';
import { languageToLocaleMap } from './timeUtils';

const isValidDate = (dateTimeIsoString: string) =>
  dateTimeIsoString && dateTimeIsoString !== '0001-01-01T00:00:00Z';

interface TimeAgoProps {
  date: string;
  locale: Locale;
  justNowLabel?: string;
  actionLabel?: string;
}

const TimeAgo = ({ date, justNowLabel, actionLabel, locale }: TimeAgoProps) => {
  const { t } = useTranslations(translations);
  const [timeAgo, setTimeAgo] = useState('');

  const updateTimeAgo = useCallback(() => {
    if (isValidDate(date)) {
      setTimeAgo(new Date(date).toISOString());
    }
  }, [date]);

  useEffect(() => {
    updateTimeAgo();
    const interval = setInterval(() => {
      updateTimeAgo();
    }, 60 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, [updateTimeAgo]);

  if (!isValidDate(timeAgo)) {
    return null;
  }

  const normalizedDate = isFuture(new Date(timeAgo))
    ? Date.now()
    : new Date(timeAgo);

  const isJustNow = differenceInSeconds(Date.now(), normalizedDate) < 60;

  const options: Intl.DateTimeFormatOptions = {
    hourCycle: 'h12',
    hour: 'numeric',
    minute: '2-digit',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const exactDateTime = new Date(timeAgo).toLocaleDateString(locale, options);

  const renderTooltipWrapper = (children: ReactNode) => (
    <TooltipRenderer
      id={date}
      placement="top"
      tooltip={<Text>{exactDateTime}</Text>}
    >
      {({ triggerProps }) => (
        <Box {...triggerProps} component="span">
          {children}
        </Box>
      )}
    </TooltipRenderer>
  );

  if (isJustNow) {
    if (justNowLabel) {
      return renderTooltipWrapper(justNowLabel);
    }
    return renderTooltipWrapper(t('Just now'));
  }

  if (actionLabel) {
    return renderTooltipWrapper(
      t('action wordsToNow ago', {
        wordsToNow: distanceInWordsToNowStrict(normalizedDate, {
          locale: languageToLocaleMap[locale],
        }),
        action: actionLabel,
      }),
    );
  }

  return renderTooltipWrapper(
    t('wordsToNow ago', {
      wordsToNow: distanceInWordsToNowStrict(normalizedDate, {
        locale: languageToLocaleMap[locale],
      }),
    }),
  );
};

export default TimeAgo;
